import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { assetsAPI } from '../../../api';
import AssetForm from './AssetForm';
import AssetHistoryPanel from './AssetHistoryPanel';
import AssetDocumentsPanel from './AssetDocumentsPanel';
import Layout from '../../../components/Layout';
import { TooltipProvider } from '../../../components/ui/Tooltip';
import { Button } from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';
import SlideOver from '../../../components/ui/SlideOver';
import { ArrowLeft, Edit, Archive, RotateCcw, Package } from 'lucide-react';
import {
  ASSET_CATEGORIES,
  ASSET_STATUSES,
  LOCATION_TYPES,
  VAT_RECLAIM_STATUSES,
  labelFor,
  formatAssetDate,
  formatAssetCurrency,
} from '../../../lib/assetConstants';
import { useToast } from '../../../hooks/useToast';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'history', label: 'History' },
  { id: 'documents', label: 'Documents' },
];

const DetailRow = ({ label, value }) => (
  <div className="py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
    <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</dt>
    <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{value ?? '—'}</dd>
  </div>
);

const AssetDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditSlideOver, setShowEditSlideOver] = useState(false);

  const fetchAsset = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError('');
      const data = await assetsAPI.getOne(id);
      setAsset(data.asset);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to load asset';
      setError(msg);
      setAsset(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAsset();
  }, [fetchAsset]);

  const handleEditSuccess = () => {
    setShowEditSlideOver(false);
    fetchAsset();
    addToast({ type: 'success', message: 'Asset updated' });
  };

  const handleArchive = async () => {
    if (!confirm('Archive this asset? It will be hidden from the default list.')) return;
    try {
      await assetsAPI.archive(id);
      addToast({ type: 'success', message: 'Asset archived' });
      fetchAsset();
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Failed to archive' });
    }
  };

  const handleRestore = async () => {
    try {
      await assetsAPI.restore(id);
      addToast({ type: 'success', message: 'Asset restored' });
      fetchAsset();
    } catch (err) {
      addToast({ type: 'error', message: err.message || 'Failed to restore' });
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col items-center justify-center py-16">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-[var(--ds-accent-primary)]" />
            <p className="mt-4 text-body text-[var(--ds-text-muted)]">Loading asset…</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !asset) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">
            {error || 'Asset not found'}
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/tenant/assets')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Assets
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <TooltipProvider>
      <Layout>
        <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="md:flex md:items-center md:justify-between">
              <div className="flex-1 min-w-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/tenant/assets')}
                  className="mb-3 text-[var(--ds-text-muted)]"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to Assets
                </Button>
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-lg bg-[rgba(59,130,246,0.15)] flex items-center justify-center">
                    <Package className="h-6 w-6 text-[var(--ds-accent-primary)]" />
                  </div>
                  <div>
                    <h1 className="text-display text-[var(--ds-text-primary)] font-bold">{asset.name}</h1>
                    <p className="text-meta text-[var(--ds-text-secondary)] font-mono">
                      {asset.assetRef}
                      {asset.isArchived && (
                        <span className="ml-2 text-xs px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700">Archived</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex md:mt-0 gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => setShowEditSlideOver(true)}>
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                {!asset.isArchived ? (
                  <Button variant="outline" size="sm" onClick={handleArchive}>
                    <Archive className="h-4 w-4 mr-1" />
                    Archive
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={handleRestore}>
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Restore
                  </Button>
                )}
              </div>
            </div>

            <div className="mt-6 border-b border-gray-200 dark:border-gray-700">
              <nav className="flex gap-6 -mb-px">
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-[var(--ds-accent-primary)] text-[var(--ds-accent-primary)]'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-white dark:bg-gray-800">
                <CardContent className="p-6">
                  <h2 className="text-h1 font-semibold text-gray-900 dark:text-gray-100 mb-4">Details</h2>
                  <dl>
                    <DetailRow label="Category" value={labelFor(ASSET_CATEGORIES, asset.category)} />
                    <DetailRow label="Status" value={labelFor(ASSET_STATUSES, asset.status)} />
                    <DetailRow label="Manufacturer" value={asset.manufacturer} />
                    <DetailRow label="Model" value={asset.model} />
                    <DetailRow label="Serial number" value={asset.serialNumber} />
                    <DetailRow label="Description" value={asset.description} />
                  </dl>
                </CardContent>
              </Card>

              <Card className="bg-white dark:bg-gray-800">
                <CardContent className="p-6">
                  <h2 className="text-h1 font-semibold text-gray-900 dark:text-gray-100 mb-4">Location & assignment</h2>
                  <dl>
                    <DetailRow label="Location" value={labelFor(LOCATION_TYPES, asset.locationType)} />
                    <DetailRow label="Location notes" value={asset.locationNotes} />
                    <DetailRow label="Assigned user" value={asset.assignedUserName || asset.assignedUserEmail} />
                    <DetailRow label="Assigned vehicle" value={asset.assignedVehicleReg} />
                  </dl>
                </CardContent>
              </Card>

              <Card className="bg-white dark:bg-gray-800">
                <CardContent className="p-6">
                  <h2 className="text-h1 font-semibold text-gray-900 dark:text-gray-100 mb-4">Purchase</h2>
                  <dl>
                    <DetailRow label="Purchase date" value={formatAssetDate(asset.purchaseDate)} />
                    <DetailRow label="Supplier" value={asset.purchaseSupplierName} />
                    <DetailRow label="Receipt ref" value={asset.purchaseReceiptRef} />
                    <DetailRow label="Net" value={formatAssetCurrency(asset.purchaseNetAmount)} />
                    <DetailRow label="VAT" value={formatAssetCurrency(asset.purchaseVatAmount)} />
                    <DetailRow label="Gross" value={formatAssetCurrency(asset.purchaseGrossAmount)} />
                  </dl>
                </CardContent>
              </Card>

              <Card className="bg-white dark:bg-gray-800">
                <CardContent className="p-6">
                  <h2 className="text-h1 font-semibold text-gray-900 dark:text-gray-100 mb-4">Warranty & service</h2>
                  <dl>
                    <DetailRow label="Warranty start" value={formatAssetDate(asset.warrantyStartDate)} />
                    <DetailRow label="Warranty expiry" value={formatAssetDate(asset.warrantyExpiryDate)} />
                    <DetailRow label="Provider" value={asset.warrantyProvider} />
                    <DetailRow label="Last calibration" value={formatAssetDate(asset.lastCalibrationDate)} />
                    <DetailRow label="Calibration due" value={formatAssetDate(asset.calibrationDueDate)} />
                    <DetailRow label="Service due" value={formatAssetDate(asset.serviceDueDate)} />
                    <DetailRow label="Service interval" value={asset.serviceIntervalMonths ? `${asset.serviceIntervalMonths} months` : null} />
                  </dl>
                </CardContent>
              </Card>

              <Card className="bg-white dark:bg-gray-800">
                <CardContent className="p-6">
                  <h2 className="text-h1 font-semibold text-gray-900 dark:text-gray-100 mb-4">Valuation</h2>
                  <dl>
                    <DetailRow label="Current value" value={formatAssetCurrency(asset.currentValue)} />
                    <DetailRow label="Replacement value" value={formatAssetCurrency(asset.replacementValue)} />
                  </dl>
                </CardContent>
              </Card>

              <Card className="bg-white dark:bg-gray-800">
                <CardContent className="p-6">
                  <h2 className="text-h1 font-semibold text-gray-900 dark:text-gray-100 mb-4">QR label</h2>
                  <dl>
                    <DetailRow label="Asset ref" value={asset.assetRef} />
                    <DetailRow
                      label="Scan URL"
                      value={
                        asset.qrScanToken ? (
                          <span className="break-all text-xs">
                            {`${window.location.origin}/tenant/assets/scan/${asset.qrScanToken}`}
                          </span>
                        ) : (
                          '—'
                        )
                      }
                    />
                  </dl>
                  {asset.qrScanToken && (
                    <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                      Print this ref on asset labels; scanning opens the asset in SparxMate when signed in.
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-white dark:bg-gray-800">
                <CardContent className="p-6">
                  <h2 className="text-h1 font-semibold text-gray-900 dark:text-gray-100 mb-4">VAT reclaim review</h2>
                  <dl>
                    <DetailRow label="Owned before SparxMate" value={asset.ownedBeforeSparxmate ? 'Yes' : 'No'} />
                    <DetailRow
                      label="Pre-reg eligibility flag"
                      value={asset.eligibleForPreRegistrationVatReclaim ? 'Yes' : 'No'}
                    />
                    <DetailRow label="Review status" value={labelFor(VAT_RECLAIM_STATUSES, asset.vatReclaimStatus)} />
                    <DetailRow label="Reviewed at" value={formatAssetDate(asset.vatReclaimReviewedAt)} />
                    <DetailRow label="Excluded reason" value={asset.vatReclaimExcludedReason} />
                    <DetailRow label="Notes" value={asset.vatReclaimNotes} />
                  </dl>
                </CardContent>
              </Card>

              {asset.notes && (
                <Card className="bg-white dark:bg-gray-800 lg:col-span-2">
                  <CardContent className="p-6">
                    <h2 className="text-h1 font-semibold text-gray-900 dark:text-gray-100 mb-2">Notes</h2>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{asset.notes}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {activeTab === 'history' && <AssetHistoryPanel assetId={id} />}
          {activeTab === 'documents' && <AssetDocumentsPanel assetId={id} />}
        </div>

        <SlideOver
          open={showEditSlideOver}
          onOpenChange={setShowEditSlideOver}
          title="Edit asset"
          width="w-full max-w-3xl"
        >
          <AssetForm asset={asset} onClose={() => setShowEditSlideOver(false)} onSuccess={handleEditSuccess} />
        </SlideOver>
      </Layout>
    </TooltipProvider>
  );
};

export default AssetDetail;
